import deepEqual from 'deep-equal';
import type { IntlShape } from 'react-intl';
import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStateHook,
	createStore,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
} from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { isSearchCompassPartial } from '@atlassian/search-client/compass';
import type { AggAbTest } from '@atlassian/search-client/fetch-agg';
import { type HydratedJiraIssue } from '@atlassian/search-client/hydration/types';
import { isSearchResultJiraIssue } from '@atlassian/search-client/jira';
import {
	type ResultsSort,
	type ResultsSortField,
	type ResultsSortOrder,
	SORT_FIELD_KEY,
} from '@atlassian/search-client/results-sort';
import {
	type ProductCount,
	type QueryInfo,
	type SearchPageEdge,
	type SearchPageResults,
} from '@atlassian/search-client/search-page/types';
import { type TeamDetails } from '@atlassian/search-client/teams/types';
import type { BackendExperiment } from '@atlassian/search-experiment/types';

import { QueryState } from '../../../common/constants/analytics';
import {
	type FilterId,
	getFilterIdOrdering,
	universalTypeFilterId,
} from '../../../common/constants/filters';
import type { BooleanFilterMonad } from '../../../common/constants/filters/boolean-filter/types';
import type { CloudConfig } from '../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterMonad,
	SelectFilterOption,
	SelectFilterOptionChange,
	SelectFilterState,
} from '../../../common/constants/filters/select-filter/types';
import type {
	AllFilterStates,
	AllFilterValues,
	Filters,
} from '../../../common/constants/filters/types';
import { toEmittableFilterValue } from '../../../common/constants/filters/ugc-filter-ids';
import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { type NounKeys } from '../../../common/constants/nouns';
import { type PrimaryProductKey, ProductKeys } from '../../../common/constants/product-keys';
import { ResultsStates } from '../../../common/constants/results';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import { type LastModifiedValue } from '../../../common/constants/schemas/query-params';
import type {
	AppendResultsArgs,
	ProductResultCount,
	SearchState,
	SearchStoreContainerProps,
	SearchStoreExperience,
} from '../../../common/constants/search';
import { SearchExperience } from '../../../common/constants/search-experience';
import { type FilterAttributes } from '../../../common/utils/events/types';
import {
	serveFpsMultiSiteSearchExperiment,
	serveWebPagesInTypeFilter,
} from '../../../common/utils/experiments';
import { EmptyEntitiesError } from '../../../common/utils/fetch-error';
import { getExcludedFilterIds } from '../../../common/utils/filters/get-excluded-filters';
import { isAllProductSearch } from '../../../common/utils/is-all-product-search';
import {
	convertQueryParamsToStringArray,
	getDatasourceFromId,
	parseQueryParams,
} from '../../../common/utils/query-params';
import type { ResultsFetchError } from '../../../common/utils/results-fetch-error';
import { getTeamId } from '../../../common/utils/teams';
import { isSelectFilter } from '../../filters/base-select-filter/is-select-filter';

import { folderFilterId, siteFilterId } from './filters/filter-ids';
import { type FiltersToDisplayConfig } from './filters/types';
// eslint-disable-next-line import/order
import { FilterUtils } from './filters/filter-utils';

export const initialState: {
	abTest: undefined;
	cql: undefined;
	autoCorrection: undefined;
	product: undefined;
	requestId: undefined;
	traceId: undefined;
	// Search Input states
	inputQuery: string;
	searchQuery: string;
	originalQuery: undefined;
	// Product States
	primaryProduct: null;
	// Filter States
	filters: {};
	filterSelectionOrder: never[];
	selectedProducts: never[];
	selectedDatasource: undefined;
	availableProducts: never[];
	allFilterExcludedProducts: never[];
	jsmIncidentFilterIds: never[];
	// Rest of store states
	resultsSort: {
		field: 'relevance';
		order: 'DESC';
	};
	retryCount: number;
	queryVersion: number;
	queryUpdatedTime: undefined;
	isLoading: false;
	isJiraHydrationLoading: false;
	isCompassTeamsLoading: false;
	shownCount: number;
	results: undefined;
	resultError: undefined;
	productResultCountMap: null;
	paginationTokens: never[];
	isLoadingMore: false;
	leapQuery: {
		loading: false;
		candidates: never[];
		titleIndex: number;
	};
	requestedBackendExperiments: undefined;
	filterDropdownOpenByQueryParam: false;
	experience: 'search-page';
	wasContentClearedBySpaceChange: false;
} = {
	abTest: undefined,
	cql: undefined,
	autoCorrection: undefined,
	product: undefined,
	requestId: undefined,
	traceId: undefined,

	// Search Input states
	inputQuery: '',
	searchQuery: '',
	originalQuery: undefined,

	// Product States
	primaryProduct: null,

	// Filter States
	filters: {},
	filterSelectionOrder: [],
	selectedProducts: [],
	selectedDatasource: undefined,
	availableProducts: [],
	allFilterExcludedProducts: [],
	jsmIncidentFilterIds: [],

	// Rest of store states
	resultsSort: {
		field: 'relevance',
		order: 'DESC',
	},
	retryCount: 0,
	queryVersion: 0,
	queryUpdatedTime: undefined,
	isLoading: false,
	isJiraHydrationLoading: false,
	isCompassTeamsLoading: false,
	shownCount: 0,
	results: undefined,
	resultError: undefined,
	productResultCountMap: null,
	paginationTokens: [],
	isLoadingMore: false,
	leapQuery: {
		loading: false,
		candidates: [],
		titleIndex: 0,
	},
	requestedBackendExperiments: undefined,
	filterDropdownOpenByQueryParam: false,
	experience: SearchExperience.SEARCH_PAGE,
	wasContentClearedBySpaceChange: false,
} satisfies SearchState;

const getUpdatedFilterSelectionOrder = (
	previousOrder: string[],
	newFilterId: string,
	selected?: boolean,
) => {
	let filterSelectionOrder = new Set([...previousOrder, newFilterId]);
	if (!selected) {
		filterSelectionOrder.delete(newFilterId);
	}

	return [...filterSelectionOrder];
};

const isFiltersDiscoverability2Enabled = (primaryProduct: PrimaryProductKey | undefined) => {
	if (!primaryProduct) {
		return false;
	}

	return primaryProduct === ProductKeys.Confluence || primaryProduct === ProductKeys.Jira;
};

/**
 * Only these keys can get set before the updateQueryParams loop when updateQueryParams=true in setSearchStoreState
 */
const safeStateKeys = [
	'filterSelectionOrder',
	'resultsSort',
	'retryCount',
	'selectedDatasource',
	'wasContentClearedBySpaceChange',
];

type SetSearchStoreStateOptions = {
	updateQueryParams?: boolean;
};
/**
 * Please use setSearchStoreState instead of setState within the Search Store controller.
 *
 * This is because we treat the URL as the source of truth for some state. Filter value states are set in the URL directly and then we listen to URL state changs to represent them in our state.
 *
 * @param newState - state properties to set
 * @param {boolean} options.updateQueryParams - [false] Whether a setState call should trigger an URL update
 *
 * Notes on updateQueryParams:
 *
 * When false, this function acts like a standard setState
 * When true, this function will allow only the {safeStateKeys} to be set and will then use the provided query/filter etc state to set the URL directly, relying on the URL observer to complete the loop
 *
 * Non-url updates should NEVER trigger the setQueryParams callback. Otherwise, this will cause infinite loops or extra render cycles.
 **/
const setSearchStoreState =
	(
		newState: Partial<SearchState>,
		options?: SetSearchStoreStateOptions,
	): Action<SearchState, SearchStoreContainerProps> =>
	(
		{ getState, setState },
		{
			setQueryParams,
			allFilters,
			config,
			availableProducts,
			primaryProduct,
			queryParams: currentQueryParams,
		},
	) => {
		// TODO future refactoring: we should instead query the newState object for any URL-state changes, then clean up the setState we perform, then update the URL. Then a consumer of this function will not need to care about any of this and can simply pass `newState` as expected
		if (!options?.updateQueryParams) {
			setState({ ...newState, availableProducts });

			return;
		}

		// We still want to set variables in state that do NOT participate in the URL
		// Initially we'll allow-list this set, as the set of filters (the disallow set) will grow over time
		const safeNewState = Object.fromEntries(
			Object.entries(newState).filter(([key]) => safeStateKeys.includes(key)),
		);

		if (Object.keys(safeNewState).length > 0) {
			setState(safeNewState);
		}

		const {
			searchQuery: newSearchQuery,
			inputQuery: newInputQuery,
			filterDropdownOpenByQueryParam,
		} = newState;
		const { inputQuery: oldInputQuery } = getState();

		const {
			selectedProducts,
			selectedDatasource,
			filters: allFilterStates,
		} = {
			...getState(),
			...newState,
		} satisfies SearchState;

		// Compute excluded filters via FILTER_EXCLUSION_RULES + FILTER_INCLUSION_RULES (e.g.
		// salesforceStage when type≠deal). Excluded filters do NOT contribute to URL params
		// or the GraphQL query. This is the single source of truth for filter visibility;
		// UI selectors (useFiltersToDisplay, useSelectedFilterCount) apply the same exclusion.
		const excludedFilterIds = getExcludedFilterIds(selectedProducts[0], allFilterStates);

		const staticFilters = Object.values(allFilters).filter((f) => {
			if (excludedFilterIds.has(f.id)) {
				return false;
			}
			const filterState = f.getInitialState(config).getState();
			const isSuggestedFilter = f.isSuggestedFilter?.(primaryProduct);

			return isAllProductSearch(selectedProducts)
				? filterState.universal || isSuggestedFilter // When there are no selected products, only universal and suggested filters are persisted in state
				: // Forge connectors are registered dynamically and not in filterState.products, so check them separately
					(filterState.universal && selectedProducts.some(isForgeConnectorProductKey)) ||
						filterState.products.some((product) => selectedProducts.includes(product));
		});

		const filterQueryParamValues = staticFilters.map((staticFilter) => {
			const filterState = allFilterStates[staticFilter.id];

			if (!staticFilter.isCompatible(filterState?.id)) {
				return {};
			}

			return staticFilter.withState(filterState, config).queryParams.get();
		});

		const filterQueryParams = Object.assign({}, ...filterQueryParamValues);
		const text = [newSearchQuery, newInputQuery, oldInputQuery].find(Boolean);
		const enableRovoSearchOverrides = Boolean(
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.getExperimentValue('enable-rovo_search_overrides', 'value', false),
		);

		// If overrides is already an object (parsed), convert it back to JSON string for parseQueryParams
		const overridesToPreserve =
			currentQueryParams?.overrides && enableRovoSearchOverrides
				? typeof currentQueryParams.overrides === 'string'
					? currentQueryParams.overrides
					: encodeURIComponent(JSON.stringify(currentQueryParams.overrides))
				: undefined;

		const paramsToPassToParse = {
			product: selectedProducts[0],
			datasource: selectedDatasource?.datasourceId,
			text: text !== '' ? text : undefined,
			...(isFiltersDiscoverability2Enabled(primaryProduct) && {
				filterDropdownOpen: filterDropdownOpenByQueryParam || undefined,
			}),
			...filterQueryParams,
			// Preserve overrides from current query params if enable-rovo_search_overrides is enabled
			...(overridesToPreserve && {
				overrides: overridesToPreserve,
			}),
		};

		const parsedQueryParams = parseQueryParams(paramsToPassToParse, { commaSeparatedValues: true });
		setQueryParams(parsedQueryParams);
	};
export const unsafe__testingOnly_setSearchStoreState: (
	newState: Partial<SearchState>,
	options?: SetSearchStoreStateOptions,
) => Action<SearchState, SearchStoreContainerProps> = setSearchStoreState;

const privateStoreActions = {
	initialiseStore:
		(): Action<SearchState, SearchStoreContainerProps> =>
		async (
			{ dispatch },
			{
				allFilters,
				queryParams,
				config,
				setQueryParams,
				availableProducts,
				allFilterExcludedProducts,
				primaryProduct,
				thirdPartyConfigs,
				experience,
			},
		) => {
			const { getSelectedProducts, filtersByProduct, isMissingProductSelection } = FilterUtils(
				allFilters,
				{},
			);
			const selectedProducts = getSelectedProducts(queryParams);

			const cql = queryParams.cql?.toString();

			if (isMissingProductSelection(queryParams)) {
				setQueryParams({ ...queryParams, product: selectedProducts[0] });
				return;
			}

			const { staticFilterValues: staticFiltersByProduct } = filtersByProduct(
				selectedProducts,
				experience,
				primaryProduct,
			);

			const resolvedQueryParams = await Promise.allSettled(
				staticFiltersByProduct.map((staticFilter: AllFilterValues) => {
					const queryParamsKeys = [staticFilter.queryParams.key];
					if (staticFilter.isSupportCustomValue && staticFilter.customValueFields) {
						queryParamsKeys.push(...staticFilter.customValueFields);
					}
					return staticFilter
						.getInitialState(config)
						.queryParams.set(convertQueryParamsToStringArray(queryParams, queryParamsKeys));
				}),
			);

			const filterStatesByProduct = resolvedQueryParams.flatMap((promise) => {
				switch (promise.status) {
					case 'fulfilled':
						return [promise.value.getState()];
					case 'rejected':
						return [];
				}
			});

			const { filterStates } = FilterUtils(staticFiltersByProduct, filterStatesByProduct);

			dispatch(
				setSearchStoreState(
					{
						filters: filterStates,
						selectedProducts,
						selectedDatasource: getDatasourceFromId(
							queryParams.datasource?.toString(),
							selectedProducts[0],
							thirdPartyConfigs,
						),
						inputQuery: String(queryParams.text ?? ''),
						searchQuery: String(queryParams.text ?? ''),
						originalQuery: String(queryParams.originalQuery ?? ''),
						...(isFiltersDiscoverability2Enabled(primaryProduct) && {
							filterDropdownOpenByQueryParam: Boolean(queryParams.filterDropdownOpen),
						}),
						availableProducts,
						allFilterExcludedProducts,
						cql,
						primaryProduct,
						experience,
					},
					{
						updateQueryParams: false, // onInit should not update the query params
					},
				),
			);
		},
	updateStore:
		(): Action<SearchState, SearchStoreContainerProps> =>
		async (
			{ dispatch, getState },
			{
				allFilters,
				queryParams,
				config,
				availableProducts,
				primaryProduct,
				thirdPartyConfigs,
				experience,
			},
		) => {
			const { filters: filterStates } = getState();

			const { getSelectedProducts, filtersByProduct } = FilterUtils(allFilters, filterStates);
			const selectedProducts = getSelectedProducts(queryParams);
			const cql = queryParams.cql?.toString();

			const { staticFilterValues: staticFiltersByProduct, filterStates: filterStatesByProduct } =
				filtersByProduct(selectedProducts, experience, primaryProduct);

			const resolvedQueryParams = await Promise.allSettled(
				staticFiltersByProduct.map((staticFilter: AllFilterValues) => {
					const filterState = filterStatesByProduct[staticFilter.id];
					const queryParamsKeys = [staticFilter.queryParams.key];
					if (staticFilter.isSupportCustomValue && staticFilter.customValueFields) {
						queryParamsKeys.push(...staticFilter.customValueFields);
					}
					const queryParamStringArray = convertQueryParamsToStringArray(
						queryParams,
						queryParamsKeys,
					);

					if (!staticFilter.isCompatible(filterState?.id)) {
						return staticFilter.getInitialState(config).queryParams.set(queryParamStringArray);
					}

					return staticFilter.withState(filterState, config).queryParams.set(queryParamStringArray);
				}),
			);

			const fullfilledFilterStates = resolvedQueryParams.flatMap((promise) => {
				switch (promise.status) {
					case 'fulfilled':
						return [promise.value.getState()];
					case 'rejected':
						return [];
				}
			});

			const { filterStates: resolvedFilterStates } = FilterUtils(
				staticFiltersByProduct,
				fullfilledFilterStates,
			);

			// In order, ensure all filters with values are added to selection
			// Ensures filters enter selection order from resume-from-url
			// TODO: this should be done with the global ordering, but we don't have that in constants yet
			let { filterSelectionOrder } = getState();
			const { getFiltersWithSelection } = FilterUtils(staticFiltersByProduct, resolvedFilterStates);
			getFiltersWithSelection().forEach((filterId: FilterId) => {
				filterSelectionOrder = getUpdatedFilterSelectionOrder(
					filterSelectionOrder,
					filterId,
					resolvedFilterStates[filterId]?.hasSelection,
				);
			});

			const querySelectedProducts = getSelectedProducts(queryParams);
			const selectedDatasource = getDatasourceFromId(
				queryParams.datasource?.toString(),
				querySelectedProducts[0],
				thirdPartyConfigs,
			);

			const nextStateSync = {
				filters: resolvedFilterStates,
				selectedProducts: querySelectedProducts,
				selectedDatasource,
				filterSelectionOrder: filterSelectionOrder,
				inputQuery: String(queryParams.text ?? ''),
				searchQuery: String(queryParams.text ?? ''),
				originalQuery: String(queryParams.originalQuery ?? ''),
				...(isFiltersDiscoverability2Enabled(primaryProduct) && {
					filterDropdownOpenByQueryParam: Boolean(queryParams.filterDropdownOpen),
				}),
				availableProducts,
				paginationTokens: [],
				cql,
				primaryProduct,
				experience,
			} satisfies Partial<SearchState>;

			const stateBeforeSync = getState();
			const prevStateSyncComparable = {
				filters: stateBeforeSync.filters,
				selectedProducts: stateBeforeSync.selectedProducts,
				selectedDatasource: stateBeforeSync.selectedDatasource,
				filterSelectionOrder: stateBeforeSync.filterSelectionOrder,
				inputQuery: stateBeforeSync.inputQuery,
				searchQuery: stateBeforeSync.searchQuery,
				originalQuery: stateBeforeSync.originalQuery,
				...(isFiltersDiscoverability2Enabled(primaryProduct) && {
					filterDropdownOpenByQueryParam: stateBeforeSync.filterDropdownOpenByQueryParam,
				}),
				availableProducts: stateBeforeSync.availableProducts,
				paginationTokens: stateBeforeSync.paginationTokens,
				cql: stateBeforeSync.cql,
				primaryProduct: stateBeforeSync.primaryProduct,
				experience: stateBeforeSync.experience,
			} satisfies Partial<SearchState>;

			if (!deepEqual(prevStateSyncComparable, nextStateSync)) {
				dispatch(
					setSearchStoreState(
						nextStateSync,
						{ updateQueryParams: false }, // onUpdate lifecycle should never update the query params
					),
				);
			}
		},
	setFilter:
		(
			state: AllFilterStates,
			options?: {
				shouldSetQueryParams?: boolean;
				/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
				setSelectedProduct?: ProductKeys;
			},
		): Action<SearchState, SearchStoreContainerProps> =>
		async ({ dispatch, getState }, { allFilters, config }) => {
			// Get the static filter definition
			const staticFilter = allFilters[state.id];
			const { filters: allFiltersState } = getState();

			if (!staticFilter.isCompatible(staticFilter.id)) {
				return;
			}

			const filterWithState = staticFilter.withState(state, config).getState();
			dispatch(
				setSearchStoreState(
					{
						filters: {
							...allFiltersState,
							[staticFilter.id]: filterWithState,
						},
						...(options?.setSelectedProduct
							? { selectedProducts: [options?.setSelectedProduct] }
							: {}),
					},
					{
						updateQueryParams: options?.shouldSetQueryParams,
					},
				),
			);
		},
	updateFilterSelectionOrder:
		(filterId: string, selected: boolean): Action<SearchState, SearchStoreContainerProps> =>
		async ({ dispatch, getState }) => {
			const { filterSelectionOrder: oldSelectionOrder } = getState();

			const newFilterSelectionOrder = getUpdatedFilterSelectionOrder(
				oldSelectionOrder,
				filterId,
				selected,
			);

			dispatch(
				setSearchStoreState({
					filterSelectionOrder: newFilterSelectionOrder,
				}),
			);
		},
	setNewFilterStates:
		(
			newFilterStates: Record<string, AllFilterStates | undefined>,
			setSelectedProduct?: ProductKeys,
			filterSelectionOrder?: string[],
		): Action<SearchState, SearchStoreContainerProps> =>
		async ({ dispatch }) => {
			dispatch(
				setSearchStoreState(
					{
						filters: newFilterStates,
						...(setSelectedProduct !== undefined && {
							selectedProducts: [setSelectedProduct],
						}),
						...(filterSelectionOrder && { filterSelectionOrder }),
					},
					{ updateQueryParams: false },
				),
			);
			dispatch(setSearchStoreState({}, { updateQueryParams: true }));
		},
} as const;

function hasLoadInitialOptions(
	filter: SelectFilterMonad<string> | BooleanFilterMonad<string>,
): filter is SelectFilterMonad<string> {
	return (
		typeof filter === 'object' &&
		filter !== null &&
		'loadInitialOptions' in filter &&
		typeof (filter as SelectFilterMonad<string>).loadInitialOptions === 'function' &&
		'getState' in filter &&
		typeof (filter as SelectFilterMonad<string>).getState === 'function'
	);
}

export const actions = {
	loadInitialSelectFilterOptions:
		(props: {
			filterId: string;
			intl: IntlShape;
		}): Action<SearchState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config, thirdPartyConfigs }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];
			const { selectedDatasource: stateSelectedDatasource, selectedProducts } = getState();

			// When selectedDatasource is not set (e.g. no `datasource=` URL param was present),
			// derive it from thirdPartyConfigs using the selected product. This allows filters
			// that need a datasourceId (e.g. salesforceStage dynamic stage options) to fetch
			// correctly even when the datasource was not explicitly set via URL.
			//
			// Some dynamic 3P filters (those declaring `requiresTopLevel3PDatasource: true` —
			// today: salesforceStage, campaignStatus, caseStatus) read from the top-level
			// thirdPartyConfigs entry directly because it always carries a datasourceId, whereas
			// .datasources?.[0] may only be populated in multi-domain configurations.
			const shouldUseTopLevel3PDatasource =
				'requiresTopLevel3PDatasource' in staticSelectFilter &&
				!!staticSelectFilter.requiresTopLevel3PDatasource;
			const selectedDatasource =
				stateSelectedDatasource ??
				(selectedProducts[0]
					? shouldUseTopLevel3PDatasource
						? thirdPartyConfigs?.[selectedProducts[0] as ProductKeys3P]
						: thirdPartyConfigs?.[selectedProducts[0] as ProductKeys3P]?.datasources?.[0]
					: undefined);

			if (
				!staticSelectFilter.isCompatible(filterState?.id) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			// Race-condition guard: dynamic 3P filters require a resolved datasource to
			// fetch their option list. If the bootstrap config hasn't loaded yet,
			// `thirdPartyConfigs` is empty and `selectedDatasource` is undefined — in
			// which case calling `loadInitialOptions` would set `didMount = true` with
			// an empty result and the filter would never retry. Skip the call so the
			// caller (UI effect) can re-trigger once the datasource arrives.
			if (shouldUseTopLevel3PDatasource && !selectedDatasource) {
				return;
			}

			const selectedSpaceAris = Array.isArray((allFiltersState as any)?.space?.value)
				? ((allFiltersState as any).space.value as string[])
				: config.initialSpaceARIs;
			const selectedContributorAccountIds = Array.isArray(
				(allFiltersState as any)?.contributor?.value,
			)
				? ((allFiltersState as any).contributor.value as string[])
				: config.contributorAccountIds;
			const extendedConfig = {
				...config,
				// overriding the intl in config to fix a race condition with initial options and outdated translation messages
				intl: props.intl,
				selectedDatasource,
				initialSpaceARIs: selectedSpaceAris,
				contributorAccountIds: selectedContributorAccountIds,
			};
			const newFilter = staticSelectFilter.withState(filterState, extendedConfig);

			if (hasLoadInitialOptions(newFilter)) {
				await newFilter.loadInitialOptions((stateChange) => {
					dispatch(privateStoreActions.setFilter(stateChange as AllFilterStates));
				});
			}
		},
	clearSelectFilterOptions:
		(props: { filterId: string }): Action<SearchState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (
				!staticSelectFilter.isCompatible(filterState?.id) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			const newFilterState = staticSelectFilter.withState(filterState, config).clear().getState();

			dispatch(privateStoreActions.setFilter(newFilterState, { shouldSetQueryParams: true }));
			dispatch(
				privateStoreActions.updateFilterSelectionOrder(props.filterId, newFilterState.hasSelection),
			);
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);
		},
	/**
	 * Sets a select filter option.
	 *
	 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
	 *                                    is still in the new selection values and clears it if not.
	 */
	setSelectFilterOption:
		(props: {
			filterId: string;
			optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
			options?: {
				customValue?: Record<string, string>;
				/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
				setSelectedProduct?: ProductKeys;
				/** If provided, clears the dependent filter if its containerARI is not in the new selection */
				validateDependentFilterId?: string;
			};
		}): Action<SearchState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (
				!staticSelectFilter.isCompatible(filterState?.id) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			const filterWithState = staticSelectFilter.withState(filterState, config);

			if (!('setOption' in filterWithState)) {
				return;
			}

			const newFilterState = filterWithState
				.setOption(props.optionChange, props.options?.customValue)
				.getState();

			if (serveWebPagesInTypeFilter() && props.filterId === universalTypeFilterId) {
				const { selectedProducts } = getState();
				const hasWebPageSelected =
					newFilterState.hasSelection && newFilterState.value?.includes(TypeFilterValueKey.WebPage);

				if (hasWebPageSelected) {
					// When web-page type is selected, use the same logic as clicking the noun in the right panel to select the product = AtlassianWebCrawler
					dispatch(actions.unsafe_setWebPageType());
					return;
				}

				if (
					!newFilterState.hasSelection &&
					selectedProducts?.includes(ProductKeys.AtlassianWebCrawler)
				) {
					// Clear the product since type filter has been cleared. This will also clear the type filter
					// and update filter selection order through selectProductFilter's existing logic
					dispatch(setSearchStoreState({ productResultCountMap: null }));
					dispatch(actions.selectProductFilter());
					return;
				}
			}

			// When the contributor selection changes, the cached `initialOptions` (suggested values
			// shown when a dependent dropdown is opened without typing) on contributor-scoped
			// filters becomes stale: the base select filter short-circuits `loadInitialOptions`
			// when `state.didMount` is `true`, so the dropdown keeps showing pre-contributor
			// suggestions until the user actually types. Reset `didMount` /
			// `didMountPerProducts` and clear `initialOptions.options` on each contributor-scoped
			// dependent filter so the next dropdown open re-fetches with the new contributor
			// scope. Behind the same FG that controls the rest of the contributor scoping logic.
			if (props.filterId === 'contributor' && fg('rovo_fps_scope_3p_typeahead_by_contributor')) {
				const previousValue = Array.isArray((filterState as SelectFilterState | undefined)?.value)
					? ((filterState as SelectFilterState).value as string[])
					: [];
				const nextValue = Array.isArray(newFilterState.value)
					? (newFilterState.value as string[])
					: [];
				const contributorSelectionChanged = !deepEqual(
					[...previousValue].sort(),
					[...nextValue].sort(),
				);

				if (contributorSelectionChanged) {
					const contributorScopedDependents = ['content'] as const;
					contributorScopedDependents.forEach((dependentId) => {
						const dependentStaticFilter = allFilters[dependentId];
						const dependentFilterState = allFiltersState[dependentId];

						if (
							!dependentStaticFilter ||
							!dependentStaticFilter.isCompatible(dependentFilterState?.id) ||
							!isSelectFilter(dependentStaticFilter.type)
						) {
							return;
						}

						const dependentSelectFilterState = dependentFilterState as
							| SelectFilterState
							| undefined;
						if (!dependentSelectFilterState) {
							return;
						}

						const resetDependentState = {
							...dependentSelectFilterState,
							didMount: false,
							didMountPerProducts: {},
							initialOptions: {
								...dependentSelectFilterState.initialOptions,
								options: [],
								isLoading: false,
							},
						} satisfies SelectFilterState;

						dispatch(
							privateStoreActions.setFilter(resetDependentState as AllFilterStates, {
								shouldSetQueryParams: false,
							}),
						);
					});
				}
			}

			// Validate dependent filter if specified (e.g., clear content filter when space changes)
			const dependentFilterId = props.options?.validateDependentFilterId;
			let wasContentCleared = false;

			if (dependentFilterId) {
				const dependentStaticFilter = allFilters[dependentFilterId];
				const dependentFilterState = allFiltersState[dependentFilterId];

				if (
					dependentStaticFilter?.isCompatible(dependentFilterState?.id) &&
					isSelectFilter(dependentStaticFilter.type)
				) {
					// Get new values from the updated filter state
					const newValues: string[] = Array.isArray(newFilterState.value)
						? newFilterState.value
						: [];

					const { selectedProducts } = getState();
					const shouldResetLoomFolderWhenSpaceCleared =
						dependentFilterId === folderFilterId &&
						selectedProducts[0] === ProductKeys.Loom &&
						newValues.length === 0 &&
						fg('loom-rovo-search');

					// Clear the dependent filter whenever the primary filter is explicitly set to a
					// non-empty selection. Reset didMount and clear cached initialOptions so the next
					// dropdown open re-fetches dependent suggestions scoped to the new primary selection
					// instead of showing stale options from the previous selection.
					// Loom: also reset folder when all spaces are deselected (Drive→content keeps
					// content when space is empty).
					if (newValues.length > 0 || shouldResetLoomFolderWhenSpaceCleared) {
						const clearedDependentState = dependentStaticFilter
							.withState(dependentFilterState, config)
							.clear()
							.getState();

						const dependentSelectFilterState = dependentFilterState as
							| SelectFilterState
							| undefined;
						const resetDependentState: AllFilterStates = dependentSelectFilterState
							? ({
									...clearedDependentState,
									didMount: false,
									didMountPerProducts: {},
									initialOptions: {
										...(dependentSelectFilterState as SelectFilterState).initialOptions,
										options: [],
										isLoading: false,
									},
									// Reset lookup so stale typeahead results (from typing in the
									// filter dropdown before space change) don't persist alongside
									// new space-scoped suggestions when the content dropdown reopens.
									lookup: {
										...(dependentSelectFilterState as SelectFilterState).lookup,
										options: [],
										lookupResultOptions: [],
										input: '',
										isLoading: false,
										isActive: false,
									},
								} as AllFilterStates)
							: (clearedDependentState as AllFilterStates);

						dispatch(
							privateStoreActions.setFilter(resetDependentState, {
								shouldSetQueryParams: false,
							}),
						);
						dispatch(privateStoreActions.updateFilterSelectionOrder(dependentFilterId, false));
						wasContentCleared = true;
					}
				}
			}

			dispatch(
				privateStoreActions.setFilter(newFilterState, {
					shouldSetQueryParams: true,
					setSelectedProduct: props.options?.setSelectedProduct,
				}),
			);
			dispatch(
				privateStoreActions.updateFilterSelectionOrder(props.filterId, newFilterState.hasSelection),
			);
			// NOTE: Inclusion-rule-driven filters (e.g. salesforceStage when type≠deal) are
			// handled as a derived projection in setSearchStoreState (URL/GraphQL builder),
			// useFiltersToDisplay (UI), and useSelectedFilterCount (badge). Stale selections
			// for excluded filters are cleared by setSearchStoreState in the same write so
			// the store stays in sync with what is visible/applied. No imperative clear is
			// needed here.

			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
					wasContentClearedBySpaceChange: wasContentCleared,
				}),
			);
		},
	setAutoFilters:
		(
			filters: Array<{
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
				};
			}>,
		): Action<SearchState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const { filters: allFilterStates } = getState();
			const newFilterStates: Record<string, AllFilterStates> = {};

			await Promise.all(
				filters.map(async (filterConfig) => {
					const staticSelectFilter = allFilters[filterConfig.filterId];
					const filterState = allFilterStates[filterConfig.filterId];

					// We should not automatically apply the filter if it has already been selected
					if (filterState?.hasSelection) {
						return;
					}

					if (
						!staticSelectFilter.isCompatible(filterState?.id) ||
						!isSelectFilter(staticSelectFilter.type)
					) {
						return;
					}

					const filterWithState = staticSelectFilter.withState(filterState, config);

					if (!('setOption' in filterWithState)) {
						return;
					}

					const optionsToChange = Array.isArray(filterConfig.optionChange)
						? filterConfig.optionChange
						: [filterConfig.optionChange];
					const selectedOptions = optionsToChange.filter((o) => o.isSelected);

					if (selectedOptions.length === 0) {
						return;
					}

					// Build query params object with the filter's key
					const queryParamsKey = staticSelectFilter.queryParams.key;
					const queryParams: Record<string, string[]> = {
						[queryParamsKey]: selectedOptions.map((o) => o.value),
					};

					// Add custom value fields if provided
					if (filterConfig.options?.customValue) {
						Object.entries(filterConfig.options.customValue).forEach(([key, value]) => {
							queryParams[key] = [value];
						});
					}

					// Use queryParams.set which handles option hydration via fetcher
					const hydratedFilter = await filterWithState.queryParams.set(queryParams);

					newFilterStates[filterConfig.filterId] = hydratedFilter.getState();
				}),
			);

			if (Object.keys(newFilterStates).length > 0) {
				const { filterSelectionOrder: currentOrder } = getState();

				// Add new auto-applied filters to the order
				const updatedOrder = Object.keys(newFilterStates).reduce(
					(order, filterId) =>
						getUpdatedFilterSelectionOrder(
							order,
							filterId,
							newFilterStates[filterId]?.hasSelection,
						),
					currentOrder,
				);

				dispatch(
					privateStoreActions.setNewFilterStates(
						{ ...allFilterStates, ...newFilterStates },
						undefined,
						updatedOrder,
					),
				);
			}
		},
	setSelectFilterLookupInput:
		(props: { filterId: string; query: string }): Action<SearchState, SearchStoreContainerProps> =>
		async ({ getState, dispatch }, { allFilters, config }) => {
			const staticSelectFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];
			const { selectedDatasource } = getState();

			if (
				!staticSelectFilter.isCompatible(filterState?.id) ||
				!isSelectFilter(staticSelectFilter.type)
			) {
				return;
			}

			const selectedSpaceAris = Array.isArray((allFiltersState as any)?.space?.value)
				? ((allFiltersState as any).space.value as string[])
				: config.initialSpaceARIs;
			const selectedContributorAccountIds = Array.isArray(
				(allFiltersState as any)?.contributor?.value,
			)
				? ((allFiltersState as any).contributor.value as string[])
				: config.contributorAccountIds;
			const extendedConfig = {
				...config,
				selectedDatasource,
				initialSpaceARIs: selectedSpaceAris,
				contributorAccountIds: selectedContributorAccountIds,
			};
			const filterWithState = staticSelectFilter.withState(filterState, extendedConfig);

			if ('setLookupInput' in filterWithState) {
				await filterWithState.setLookupInput(props.query, (stateChange: any) => {
					dispatch(privateStoreActions.setFilter(stateChange));
				});
			}
		},
	setBooleanFilterValue:
		(
			props: {
				filterId: string;
				value: boolean;
				options?: {
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
				};
			},
			privateActions: {
				readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
				readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
				readonly setFilter: (
					state: AllFilterStates,
					options?: {
						shouldSetQueryParams?: boolean;
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					},
				) => Action<SearchState, SearchStoreContainerProps>;
				readonly updateFilterSelectionOrder: (
					filterId: string,
					selected: boolean,
				) => Action<SearchState, SearchStoreContainerProps>;
				readonly setNewFilterStates: (
					newFilterStates: Record<string, AllFilterStates | undefined>,
					setSelectedProduct?: ProductKeys,
					filterSelectionOrder?: string[],
				) => Action<SearchState, SearchStoreContainerProps>;
			} = privateStoreActions,
		): Action<SearchState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { allFilters, config }) => {
			const staticBooleanFilter = allFilters[props.filterId];
			const allFiltersState = getState().filters;
			const filterState = allFiltersState[props.filterId];

			if (!staticBooleanFilter.isCompatible(filterState?.id)) {
				return;
			}

			if (staticBooleanFilter.type !== 'boolean') {
				return;
			}

			const newBooleanFilterState = staticBooleanFilter
				.withState(filterState, config)
				.setValue(props.value)
				.getState();

			dispatch(
				privateActions.setFilter(newBooleanFilterState, {
					shouldSetQueryParams: true,
					setSelectedProduct: props.options?.setSelectedProduct,
				}),
			);
			dispatch(
				privateActions.updateFilterSelectionOrder(
					props.filterId,
					newBooleanFilterState.hasSelection,
				),
			);
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);
		},
	clearAllFilters:
		(): Action<SearchState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { allFilters, config }) => {
			const { filters: allFilterStates } = getState();
			const staticFilters = Object.values(allFilters);
			const clearedFilters = clearFilters(allFilterStates, staticFilters, config);

			dispatch(
				setSearchStoreState(
					{
						filters: clearedFilters,
						selectedProducts: [],
						filterSelectionOrder: [],
						wasContentClearedBySpaceChange: false,
					},
					{ updateQueryParams: true },
				),
			);
		},
	clearAllAvailableFilters:
		(): Action<SearchState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { allFilters, config }) => {
			const staticFilters = Object.values(allFilters);
			const { filters: allFilterStates } = getState();

			dispatch(
				setSearchStoreState(
					{
						filters: clearFilters(allFilterStates, staticFilters, config),
						filterSelectionOrder: [],
						wasContentClearedBySpaceChange: false,
					},
					{ updateQueryParams: true },
				),
			);
		},
	// Product filter actions
	selectProductFilter:
		(
			productKey?: ProductKeys,
			datasource?: ThirdPartyConfigsWithAPIResponse | string,
			// TODO: implement app filter auto selection
		): Action<SearchState, SearchStoreContainerProps> =>
		({ getState, dispatch }, { thirdPartyConfigs, allFilters, config }) => {
			const { selectedProducts, selectedDatasource, filters, filterSelectionOrder } = getState();

			// Convert string datasource to object if needed for consistency
			const datasourceObject =
				typeof datasource === 'string'
					? getDatasourceFromId(datasource, productKey, thirdPartyConfigs)
					: datasource;

			// Only return early if both product and datasource are the same
			if (
				productKey &&
				selectedProducts.includes(productKey) &&
				datasourceObject?.datasourceId === selectedDatasource?.datasourceId
			) {
				return;
			}

			const persistedFilters: Record<string, AllFilterStates> = {};

			// Persist selected universal filters if they are relevant to the selected product
			for (const [id, filter] of Object.entries(filters)) {
				if (!filter) {
					continue;
				}

				if (
					filter.universal &&
					filter.hasSelection &&
					(!productKey ||
						(productKey && filter.products.includes(productKey)) ||
						// Universal filters should always be persisted for forge connectors
						(productKey && isForgeConnectorProductKey(productKey)))
				) {
					persistedFilters[id] = filter;
				}
			}

			// Type is a universal filter, but type needs to be cleared if we're filtering away from Atlas
			// This is because selecting a global noun sets product AND type values
			if (selectedProducts.includes(ProductKeys.Atlas)) {
				delete persistedFilters[universalTypeFilterId];
			}

			const newSelectedProducts = productKey ? [productKey] : [];

			// Clear web-page type filter when navigating away from Web Pages
			if (serveWebPagesInTypeFilter()) {
				const currentTypeFilter = filters[universalTypeFilterId];
				const isWebPageTypeSelected =
					currentTypeFilter?.type === 'single-select' &&
					currentTypeFilter?.value?.includes(TypeFilterValueKey.WebPage);
				if (isWebPageTypeSelected) {
					// When user clicks "All" or selects any product, clear the web-page type
					delete persistedFilters[universalTypeFilterId];
				}
			}

			// Universal filters being persisted also retain their ordering
			const newFilterSelectionOrder = filterSelectionOrder.filter((f) => {
				return Object.keys(persistedFilters).includes(f);
			});

			dispatch(
				setSearchStoreState(
					{
						selectedProducts: newSelectedProducts,
						selectedDatasource: datasourceObject,
						filterSelectionOrder: newFilterSelectionOrder,
						filters: persistedFilters,
						resultsSort: initialState.resultsSort,
						wasContentClearedBySpaceChange: false,
					},
					{ updateQueryParams: true },
				),
			);
		},
	unselectProductFilter:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(actions.selectProductFilter(undefined));
		},
	// Search Input Actions
	setInputQuery:
		(inputQuery: string): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ inputQuery: inputQuery.trim() }));
		},
	setSearchQuery:
		(searchQuery: string): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({ searchQuery: searchQuery.trim() }, { updateQueryParams: true }),
			);
		},
	triggerSearch:
		(): Action<SearchState> =>
		({ getState, dispatch }) => {
			const { inputQuery, searchQuery, retryCount, productResultCountMap } = getState();
			dispatch(
				setSearchStoreState({
					productResultCountMap: searchQuery === inputQuery ? productResultCountMap : null,
					wasContentClearedBySpaceChange: false,
				}),
			);
			dispatch(
				setSearchStoreState(
					{
						searchQuery: inputQuery,
						retryCount: searchQuery === inputQuery ? retryCount + 1 : 0,
					},
					{ updateQueryParams: true },
				),
			);
		},
	retrySearch:
		(): Action<SearchState> =>
		({ dispatch, getState }) => {
			dispatch(setSearchStoreState({ retryCount: getState().retryCount + 1 }));
		},
	// Sorting actions
	setSortField:
		(sortField: ResultsSortField): Action<SearchState> =>
		({ dispatch, getState }) => {
			const { resultsSort } = getState();
			dispatch(
				setSearchStoreState({
					resultsSort: { ...resultsSort, field: sortField },
					paginationTokens: [],
				}),
			);
		},
	setSortOrder:
		(sortOrder: ResultsSortOrder): Action<SearchState> =>
		({ dispatch, getState }) => {
			const { resultsSort } = getState();
			dispatch(
				setSearchStoreState({
					resultsSort: { ...resultsSort, order: sortOrder },
					paginationTokens: [],
				}),
			);
		},
	resetSort:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					resultsSort: initialState.resultsSort,
					paginationTokens: [],
				}),
			);
		},
	// Rest of Search Actions
	setQueryUpdatedTime:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ queryUpdatedTime: Date.now() }));
		},
	resetQueryVersion:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ queryVersion: 0 }));
		},
	incrementQueryVersion:
		(): Action<SearchState> =>
		({ getState, setState }) => {
			const { queryVersion } = getState();
			setState({ queryVersion: queryVersion + 1 });
		},
	setIsLoading:
		(isLoading: boolean): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ isLoading }));
		},
	// Results actions
	setResults:
		(
			results?: SearchState['results'],
			error?: SearchState['resultError'],
			requestId?: SearchState['requestId'],
			traceId?: SearchState['traceId'],
		): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isLoading: false,
					results,
					resultError: error,
					requestId,
					traceId,
					wasContentClearedBySpaceChange: false,
				}),
			);
		},

	appendMoreResults:
		(results: AppendResultsArgs): Action<SearchState> =>
		({ dispatch, getState }) => {
			const { results: currentResults } = getState();
			if (!currentResults || !results) {
				return;
			}

			const currentEdges = currentResults.edges;
			const newEdges = results.edges;

			const newResults = {
				...currentResults,
				edges: currentEdges.concat(newEdges),
				pageInfo: results?.pageInfo,
			};

			dispatch(
				setSearchStoreState({
					results: newResults,
					isLoadingMore: false,
				}),
			);
		},
	setFetchFromCursor:
		(cursor: string): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					paginationTokens: [{ cursor }],
				}),
			);
		},
	setIsLoadingMore:
		(isLoadingMore: boolean): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isLoadingMore,
				}),
			);
		},
	setJiraIssueFieldsLoading:
		(isLoading: boolean): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isJiraHydrationLoading: isLoading,
				}),
			);
		},
	setJiraIssueDetails:
		(issueData: HydratedJiraIssue[]): Action<SearchState> =>
		({ dispatch, getState }) => {
			const { results } = getState();
			let hydratedResults: SearchPageEdge[] = [];
			if (results?.edges && results.edges.length > 0 && issueData.length > 0) {
				// TODO: fix colorName being passed twice with inconsistent values, see the unit test for context
				hydratedResults = [...results.edges].map((edge) => {
					if (isSearchResultJiraIssue(edge.node)) {
						const id = edge.node.id;
						const hydratedIssue = issueData.find((issue) => issue.id === id);

						if (edge.node.status && hydratedIssue) {
							edge.node.status.name = hydratedIssue.statusField.status.name;
							edge.node.status.colorName =
								hydratedIssue.statusField.status.statusCategory?.colorName;
						}
					}
					return edge;
				});
				dispatch(
					setSearchStoreState({
						results: {
							...results,
							edges: hydratedResults,
						},
					}),
				);
			}
		},
	setCompassTeamsLoading:
		(isLoading: boolean): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isCompassTeamsLoading: isLoading,
				}),
			);
		},
	setCompassTeams:
		(teamData: TeamDetails[]): Action<SearchState> =>
		({ dispatch, getState }) => {
			const { results } = getState();
			let resultsWithTeamNames: SearchPageEdge[] = [];
			if (results?.edges && results.edges.length > 0 && teamData.length > 0) {
				resultsWithTeamNames = [...results.edges].map((edge) => {
					if (isSearchCompassPartial(edge.node)) {
						const teamId = getTeamId(edge.node.ownerId);
						const team = teamData.find((team) => team.id === teamId);
						edge.node.ownerName = team?.displayName;
					}
					return edge;
				});
				dispatch(
					setSearchStoreState({
						results: {
							...results,
							edges: resultsWithTeamNames,
						},
					}),
				);
			}
		},

	resetResults:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					isLoading: false,
					shownCount: 0,
					results: undefined,
					resultError: undefined,
					requestId: undefined,
					wasContentClearedBySpaceChange: false,
				}),
			);
		},
	setAutoCorrection:
		(autoCorrection?: string): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(setSearchStoreState({ autoCorrection }));
		},
	setLeapQuery:
		({
			candidates,
			titleIndex,
			loading,
		}: {
			candidates: string[];
			titleIndex: number;
			loading: boolean;
		}): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState({
					leapQuery: {
						loading,
						candidates,
						titleIndex,
					},
				}),
			);
		},
	setProductResultCount:
		(totalCounts?: ProductCount[]): Action<SearchState> =>
		({ dispatch, getState }) => {
			if (totalCounts === undefined) {
				dispatch(
					setSearchStoreState({
						productResultCountMap: null,
					}),
				);
				return;
			}

			const {
				filters: filterStates,
				availableProducts,
				productResultCountMap: oldProductResultCountMap,
			} = getState();

			const filtersWithSelection = Object.values(filterStates).filter(
				(filter) => filter?.hasSelection,
			);

			const productsCorrespondingToAllSelectedFilters = filtersWithSelection.length
				? availableProducts.filter((product) =>
						filtersWithSelection.every((filter) => filter?.products?.includes(product)),
					)
				: availableProducts;
			const resultsCountsAdjustedForSelectedFilters = totalCounts.map((resultCount) => {
				if (
					productsCorrespondingToAllSelectedFilters.includes(resultCount.product as ProductKeys)
				) {
					return resultCount;
				} else {
					return {
						...resultCount,
						count: 0,
					};
				}
			});

			const productResultCountMap =
				resultsCountsAdjustedForSelectedFilters.reduce<ProductResultCount>(
					(acc, { product, count }) => {
						if (product) {
							acc[product] = count;
						}
						return acc;
					},
					{},
				);

			dispatch(
				setSearchStoreState({
					productResultCountMap: { ...oldProductResultCountMap, ...productResultCountMap },
				}),
			);
		},
	// For backwards compatibility
	clearHybridFilters:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(actions.clearAllFilters());
		},

	/**
	 * TODO: QS-9329: Persist auto-applied filters when selecting
	 * * Projects (type=project & product=townsquare)
	 * * Goals (type=goal & product=townsquare)
	 * in the right rail.
	 *
	 * More information: https://product-fabric.atlassian.net/browse/QS-9329
	 *
	 * Considered unsafe since it's using queryParams to change the state.
	 * Ideally we should set it directly in the state, but that requires introducing a new action.
	 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
	 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
	 *
	 * We hope to improve Atlassian Home / global noun handling after GA
	 *
	 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
	 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
	 *
	 * @param value: a noun to be set
	 */
	unsafe_setAtlasNouns:
		(value?: NounKeys): Action<SearchState, SearchStoreContainerProps> =>
		({ dispatch }, { queryParams, setQueryParams }) => {
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);

			if (!value) {
				const { product: _, type: __, ...queryParamsWithResetNounFilter } = queryParams;
				setQueryParams({ ...queryParamsWithResetNounFilter });
			} else {
				const { text, originalQuery, lastModified, contributors } = queryParams;
				// We explicitly only keep text, last modified and contributors
				setQueryParams({
					product: ProductKeys.Atlas,
					type: value,
					text: text as string,
					originalQuery: originalQuery as string,
					lastModified: lastModified as LastModifiedValue,
					contributors: contributors as string[],
				});
			}
		},
	/**
	 * * TODO: QS-9329: Persist auto-applied filters when selecting
	 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
	 * or type=web-page in horizontal filters.
	 *
	 * More information: https://product-fabric.atlassian.net/browse/QS-9329
	 *
	 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
	 * to set the Type filter to "web-page".
	 */
	unsafe_setWebPageType:
		(): Action<SearchState, SearchStoreContainerProps> =>
		({ dispatch }, { queryParams, setQueryParams }) => {
			if (!serveWebPagesInTypeFilter()) {
				// Control cohort: use the old behavior by selecting AtlassianWebCrawler product
				dispatch(actions.selectProductFilter(ProductKeys.AtlassianWebCrawler));
				return;
			}

			// Test cohort: use the new behavior by setting type = 'web-page' in the URL
			dispatch(
				setSearchStoreState({
					productResultCountMap: null,
				}),
			);

			const { text, originalQuery, lastModified, contributors } = queryParams;

			setQueryParams({
				product: ProductKeys.AtlassianWebCrawler,
				type: TypeFilterValueKey.WebPage,
				text: text as string,
				originalQuery: originalQuery as string,
				lastModified: lastModified as LastModifiedValue,
				contributors: contributors as string[],
			});
		},

	setRequestedBackendExperiments:
		(
			requestedBackendExperiments: BackendExperiment,
		): Action<SearchState, SearchStoreContainerProps> =>
		({ setState }) => {
			const experimentLayers = requestedBackendExperiments?.experimentLayers?.map((layer) => {
				const { analyticsDefinitions, ...rest } = layer;
				return {
					...rest,
					definitions: analyticsDefinitions,
				};
			});

			setState({
				requestedBackendExperiments: {
					experimentLayers,
				},
			});
		},
	setJsmIncidentFilterIds:
		(jsmIncidentFilterIds: string[]): Action<SearchState, SearchStoreContainerProps> =>
		({ getState, setState }) => {
			if (getState().jsmIncidentFilterIds === jsmIncidentFilterIds) {
				return;
			}
			setState({
				jsmIncidentFilterIds,
			});
		},
	/*
	 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
	 * This means we need to update query param to make it available to FPS
	 * That's why updateQueryParams is set to 'true'
	 *
	 * We only call it for clearing and the "set" value would be handled by onNavigate
	 * */
	unsetFilterDropdownOpen:
		(): Action<SearchState> =>
		({ dispatch }) => {
			dispatch(
				setSearchStoreState(
					{
						filterDropdownOpenByQueryParam: false,
					},
					{
						updateQueryParams: true,
					},
				),
			);
		},
} as const;

const clearFilters = (
	allFilterStates: Record<string, AllFilterStates | undefined>,
	filters: AllFilterValues[],
	config: CloudConfig,
) => {
	const clearedFilters = filters.map((filter) => {
		const filterState = allFilterStates[filter.id];

		// Clear all does not clear the multi-site selector
		if (
			filter.id === siteFilterId &&
			serveFpsMultiSiteSearchExperiment({
				primaryProduct: config.primaryProduct,
				fireExperimentExposure: false,
			})
		) {
			return filterState ? [filter.id, filterState] : [];
		}

		if (!filter.isCompatible(filterState?.id)) {
			return [];
		}

		return [filter.id, filter.withState(filterState, config).clear().getState()];
	});

	return Object.fromEntries(clearedFilters) as SearchState['filters'];
};

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<SearchState, TActions>({
	initialState,
	actions,
	name: 'search',
});

export const useFilterActions: HookActionsFunction<
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>
> = createActionsHook(Store);

export const SearchStoreContainer: OverrideContainerComponent<SearchStoreContainerProps> =
	createContainer<SearchState, TActions, SearchStoreContainerProps>(Store, {
		onInit:
			() =>
			({ dispatch }) => {
				dispatch(privateStoreActions.initialiseStore());
			},
		onUpdate:
			() =>
			({ dispatch }) => {
				dispatch(privateStoreActions.updateStore());
			},
		onCleanup:
			() =>
			({ setState }) => {
				setState(initialState);
			},
	});

// Note - This doesn't trigger a re-render
export const useSearch: HookFunction<
	SearchState,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store);

export const useSearchActions: HookActionsFunction<
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>
> = createActionsHook(Store);

export const useSearchState = createStateHook(Store, {
	selector: (state: SearchState, cb: (state: SearchState) => unknown) => cb(state),
}) as unknown as <T>(cb: (state: SearchState) => T) => T;

export const useFilters: HookStateFunction<
	Record<string, AllFilterStates | undefined>,
	void
> = createStateHook(Store, {
	selector: (state) => state.filters,
});

// Hooks
export const useInputQuery: HookFunction<
	string,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.inputQuery,
});

export const useSearchExperience: HookFunction<
	SearchStoreExperience,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.experience,
});

export const useSearchQuery: HookFunction<
	string,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.searchQuery,
});

export const useOriginalQuery: HookFunction<
	string | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.originalQuery,
});

export const useAutoCorrection: HookFunction<
	string | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.autoCorrection,
});

export const useRetryCount: HookFunction<
	number,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.retryCount,
});

export const useProductResultCountMap: HookFunction<
	ProductResultCount | null,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.productResultCountMap,
});

export const useABTest: HookFunction<
	AggAbTest | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.results?.abTest,
});

export const useQueryInfo: HookFunction<
	QueryInfo | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.results?.queryInfo,
});

export const useQueryState: HookFunction<
	QueryState,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => (state.searchQuery ? QueryState.POSTQUERY : QueryState.PREQUERY),
});

export const useQueryUpdatedTime: HookFunction<
	number | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.queryUpdatedTime,
});

export const useResults: HookFunction<
	SearchPageResults | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.results,
});

export const useResultsError: HookFunction<
	ResultsFetchError | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.resultError,
});

export const useResultsRequestId: HookFunction<
	string | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.requestId,
});

export const useResultsTraceId: HookStateFunction<string | undefined, void> = createStateHook(
	Store,
	{
		selector: (state) => state.traceId,
	},
);

export const useIsLoading: HookFunction<
	boolean,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.isLoading,
});

export const useResultsSort: HookFunction<
	ResultsSort,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.resultsSort,
});

export const useQueryVersion: HookFunction<
	number,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.queryVersion,
});

export const useAllFilterExcludedProducts: HookStateFunction<string[], void> = createStateHook(
	Store,
	{
		selector: (state) => state.allFilterExcludedProducts,
	},
);

export const useWasContentClearedBySpaceChange: HookStateFunction<boolean, void> = createStateHook(
	Store,
	{
		selector: (state) => state.wasContentClearedBySpaceChange,
	},
);

/**
 * A hook to determine the state of search results on a search page.
 *
 * NOTE: It is possible for SearchPageQuery to contain search results AND errors within the GraphQL
 * response. In this case, it is expected for FPS to display search results even if there are
 * downstream errors. To prevent this issue, `ResultsStates.RESULTS` state should ALWAYS be above
 * `ResultsState.ERROR`. See PIR-21043 for the investigation.
 *
 */
export const useResultsState: HookStateFunction<ResultsStates, void> = createStateHook(Store, {
	selector: createSelector(
		[(state) => state.results, (state) => state.isLoading, (state) => state.resultError],
		(results, isLoading, resultError): ResultsStates =>
			getResultsState({ results, isLoading, resultError }),
	),
});

type GetResultsStateProps = {
	results: SearchPageResults | undefined;
	isLoading: boolean;
	resultError: Error | undefined;
};

export const getResultsState = ({
	results,
	isLoading,
	resultError,
}: GetResultsStateProps): ResultsStates => {
	if (isLoading) {
		return ResultsStates.IS_LOADING;
	}

	if (results && results.edges && results.edges.length > 0) {
		return ResultsStates.RESULTS;
	}

	// If there are no entities for a query, an error will throw
	// when no entities for query is equivalent to no results
	if (
		resultError instanceof EmptyEntitiesError ||
		(results && (results.edges ?? []).length === 0)
	) {
		return ResultsStates.NO_RESULTS;
	}

	if (resultError) {
		return ResultsStates.ERROR;
	}

	if (!results) {
		return ResultsStates.INITIAL;
	}

	return ResultsStates.ERROR;
};

// filter hooks
export const useSelectedProducts: HookFunction<
	string[],
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.selectedProducts,
});

export const useSelectedProduct: HookFunction<
	string,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.selectedProducts[0],
});

export const useSelectedDatasource: HookFunction<
	ThirdPartyConfigsWithAPIResponse | undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.selectedDatasource,
});

export const useIsJiraHydrationLoading: HookStateFunction<boolean | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.isJiraHydrationLoading,
	});

export const useIsCompassTeamsLoading: HookStateFunction<boolean | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.isCompassTeamsLoading,
	});

export const useIsSearchReady: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.availableProducts && state.availableProducts.length > 0,
});

export const usePageEndCursor: HookStateFunction<string | null | undefined, void> = createStateHook(
	Store,
	{
		selector: (state) => state.results?.pageInfo?.endCursor,
	},
);

export const useFetchFromCursor: HookStateFunction<string, void> = createStateHook(Store, {
	selector: (state) => state.paginationTokens[0]?.cursor,
});

export const useIsLoadingMore: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.isLoadingMore,
});

export const useProducts: HookStateFunction<
	{
		selectedProduct: string;
		filteredProducts: string[];
		availableProducts: string[];
	},
	void
> = createStateHook(Store, {
	selector: createSelector(
		[(state) => state.selectedProducts, (state) => state.availableProducts],
		(selectedProducts, availableProducts) => {
			return {
				selectedProduct: selectedProducts[0],
				filteredProducts: selectedProducts[0] ? [selectedProducts[0]] : availableProducts,
				availableProducts,
			};
		},
	),
});

// Pure functions extracted to break React dependency chain for SSR (QS-9423)
// Consumers should import directly from './get-type-entities'
import type { UseTypeEntitiesResult } from './get-type-entities';
import { getUniversalTypeEntities } from './get-universal-type-entities';

export const useTypeEntities: HookStateFunction<
	UseTypeEntitiesResult,
	{
		thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	}
> = createStateHook(Store, {
	selector: createSelector(
		[
			(state) => state.availableProducts,
			(state) => state.selectedProducts,
			(state) => state.allFilterExcludedProducts,
			(state) => state.filters[universalTypeFilterId],
			(_, props: { thirdPartyConfigs: ThirdPartyConfigsBootstrap }) => props,
		],
		(
			availableProducts,
			selectedProducts,
			allFilterExcludedProducts,
			typeFilter,
			props,
		): UseTypeEntitiesResult => {
			const { thirdPartyConfigs } = props;

			// Filter out excluded products from "All" filter
			const allFilterIncludedProducts = availableProducts.filter(
				(product) => !allFilterExcludedProducts.includes(product),
			);

			const productsToSearch = selectedProducts[0]
				? [selectedProducts[0]]
				: allFilterIncludedProducts;

			return getUniversalTypeEntities(
				productsToSearch,
				typeFilter,
				thirdPartyConfigs,
				availableProducts,
				selectedProducts,
			);
		},
	),
});

export const useSelectFilterStore: HookFunction<
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'assignee';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'reporter';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'createdBy';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'content';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'contributor';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'lastUpdated';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'project';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'space';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'universalTypeFilter';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'focusAreaType';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'focusAreaUnder';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'owner';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'label';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'pageStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'lastUpdatedBy';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'statusFilter';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'assetsSchema';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'assetsType';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'positionFocusArea';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'positionStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'positionJobFamily';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'positionTeam';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'positionEmploymentType';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'sort';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'campaignStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'caseStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'documentStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'calendarType';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'leadStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'taskStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'taskType';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'salesforceStage';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'pullRequestStatus';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'repository';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'reviewer';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'slackChannel';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'site';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| {
			selectedProducts: string[];
			availableProducts: string[];
			allFilterExcludedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			experience: SearchStoreExperience;
			id: 'folder' | 'workspaceName';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMount: boolean;
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
	  }
	| undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	{
		filterId: string;
	}
> = createHook(Store, {
	selector: (state, props: { filterId: string }) => {
		const filter = state.filters[props.filterId];

		if (filter === undefined) {
			return undefined;
		}

		if (!(filter.type === 'multi-select' || filter.type === 'single-select')) {
			throw new Error('Not a select filter');
		}

		return {
			...filter,
			selectedProducts: state.selectedProducts,
			availableProducts: state.availableProducts,
			allFilterExcludedProducts: state.allFilterExcludedProducts,
			primaryProduct: state.primaryProduct,
			experience: state.experience,
		};
	},
});

export const useBooleanFilterStore: HookFunction<
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'watchedByMe';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'commentedByMe';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'verified';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'onlyTitleMatch';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'archived';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'compassArchived';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| {
			selectedProducts: string[];
			primaryProduct: PrimaryProductKey | null;
			id: 'hasAttachment';
			queryParams: {
				key: string;
			};
			products: ProductKeys[];
			type: 'boolean';
			value: boolean;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
	  }
	| undefined,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	{
		filterId: string;
	}
> = createHook(Store, {
	selector: (state, props: { filterId: string }) => {
		const filter = state.filters[props.filterId];

		if (filter === undefined) {
			return undefined;
		}

		if (filter?.type !== 'boolean') {
			throw new Error('Not a boolean filter');
		}

		return {
			...filter,
			selectedProducts: state.selectedProducts,
			primaryProduct: state.primaryProduct,
		};
	},
});

export const getUniversalFilters = (filters: Filters): AllFilterStates[] => {
	const filterIdOrdering = getFilterIdOrdering();
	return filterIdOrdering.flatMap((f) => {
		const filter = filters[f];
		return filter && filter.universal ? [filter] : [];
	});
};

export const getFiltersByPrimaryProduct = (
	filters: Filters,
	primaryProduct: PrimaryProductKey,
): AllFilterStates[] => {
	const filterIdOrdering = getFilterIdOrdering();
	return filterIdOrdering.flatMap((f) => {
		const filter = filters[f];
		return filter &&
			filter.products.find((relevantProductFilter) => relevantProductFilter === primaryProduct)
			? [filter]
			: [];
	});
};

export const getSelectedProductFilters = (
	filters: Filters,
	selectedProducts: ProductKeys[],
): AllFilterStates[] => {
	const filterIdOrdering = getFilterIdOrdering();
	return filterIdOrdering.flatMap((f) => {
		const filter = filters[f];

		// Universal filters should always be shown for forge connectors
		// This is needed because forge connectors are dynamically loaded and not in the static products list
		if (filter && filter.universal && selectedProducts.some(isForgeConnectorProductKey)) {
			return [filter];
		}

		if (filter && f === 'content' && selectedProducts.includes(ProductKeys.ConfluenceDC)) {
			return fg('confluence-dc-content-filter') ? [filter] : [];
		} else {
			return filter && selectedProducts.some((product) => filter?.products.includes(product))
				? [filter]
				: [];
		}
	});
};

/**
 * All available filters in the initial ordering
 * Configurable based on the primary product - this is the case for QF filters
 */
const getAllAvailableFilters = (
	filters: Filters,
	selectedProducts: ProductKeys[],
	config?: import('./filters/types').FiltersToDisplayConfig,
) => {
	if (config?.basedOnPrimaryProduct && config?.primaryProduct) {
		return isAllProductSearch(selectedProducts)
			? getFiltersByPrimaryProduct(filters, config?.primaryProduct)
			: getSelectedProductFilters(filters, selectedProducts);
	}

	return isAllProductSearch(selectedProducts)
		? getUniversalFilters(filters)
		: getSelectedProductFilters(filters, selectedProducts);
};

/**
 * All available filters are always returned for inline, but the ordering depends on selection
 * The caller UI is responsible for deciding how many can be rendered by available width
 */
const getFilterIdsForInline = (
	filters: Filters,
	selectedProducts: ProductKeys[],
	filterSelectionOrder: string[],
	config?: import('./filters/types').FiltersToDisplayConfig,
): string[] => {
	const allAvailableFilterIds = getAllAvailableFilters(filters, selectedProducts, config).map(
		(a) => a.id as string,
	);

	// using the selection order, filter from the available (removes any invalid filters to show)
	const filtersForInline = filterSelectionOrder.filter((f) => allAvailableFilterIds.includes(f));
	// Filters with no selections can also be prioritised based on the props
	const filtersPrioritised =
		config?.prioritisedFilterIds?.filter((f) => allAvailableFilterIds.includes(f)) ?? [];

	// then simply add the rest back (with a set for deduplication)
	const inline = [
		...new Set([...filtersForInline, ...filtersPrioritised, ...allAvailableFilterIds]),
	];

	return inline;
};

export const useFiltersToDisplay: HookFunction<
	{
		filtersInDropdown: (
			| 'content'
			| 'archived'
			| 'assignee'
			| 'reporter'
			| 'commentedByMe'
			| 'contributor'
			| 'createdBy'
			| 'lastUpdated'
			| 'project'
			| 'space'
			| 'site'
			| 'verified'
			| 'onlyTitleMatch'
			| 'universalTypeFilter'
			| 'focusAreaType'
			| 'focusAreaUnder'
			| 'owner'
			| 'label'
			| 'pageStatus'
			| 'lastUpdatedBy'
			| 'statusFilter'
			| 'compassArchived'
			| 'assetsSchema'
			| 'assetsType'
			| 'watchedByMe'
			| 'positionStatus'
			| 'positionEmploymentType'
			| 'positionJobFamily'
			| 'positionTeam'
			| 'positionFocusArea'
			| 'sort'
			| 'salesforceStage'
			| 'campaignStatus'
			| 'caseStatus'
			| 'documentStatus'
			| 'calendarType'
			| 'leadStatus'
			| 'taskStatus'
			| 'taskType'
			| 'pullRequestStatus'
			| 'repository'
			| 'reviewer'
			| 'slackChannel'
			| 'folder'
			| 'workspaceName'
			| 'hasAttachment'
		)[];
		filtersInLine: string[];
		filterSelectionOrder: string[];
	},
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	FiltersToDisplayConfig | undefined
> = createHook(Store, {
	selector: (
		{ filters, selectedProducts, filterSelectionOrder },
		config?: import('./filters/types').FiltersToDisplayConfig,
	) => {
		// Apply exclusion + inclusion rules (single source of truth for filter visibility):
		// excluded filters are hidden from both the inline filter row and the dropdown list.
		// setSearchStoreState clears their underlying selection in the same write so the store
		// stays in sync with what is visible/applied to URL/GraphQL/badge count.
		const excludedFilterIds = getExcludedFilterIds(selectedProducts[0], filters);
		const isExcluded = (id: string) => excludedFilterIds.has(id as FilterId);

		// All filters always show in the dropdown, in order (minus excluded)
		const filtersInDropdown = getAllAvailableFilters(filters, selectedProducts, config)
			.map((f) => f.id)
			.filter((id) => !isExcluded(id as string));

		const filtersInLine = getFilterIdsForInline(
			filters,
			selectedProducts,
			filterSelectionOrder,
			config,
		).filter((id) => !isExcluded(id));

		return {
			filtersInDropdown,
			filtersInLine,
			filterSelectionOrder,
		};
	},
});

export const useSelectedFilterCount: HookFunction<
	{
		selectedFilterCount: number;
	},
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: ({ selectedProducts, filters, resultsSort, primaryProduct }) => {
		// Apply exclusion + inclusion rules (single source of truth for filter visibility):
		// excluded filters do not contribute to the badge count. setSearchStoreState
		// clears their underlying selection so the store stays in sync.
		const excludedFilterIds = getExcludedFilterIds(selectedProducts[0], filters);
		const isMultiSiteSearchEnabled = serveFpsMultiSiteSearchExperiment({
			primaryProduct,
			fireExperimentExposure: false,
		});
		const selectedFilters = Object.values(filters)
			.filter((filter) => filter?.hasSelection)
			.filter((filter) => filter && !excludedFilterIds.has(filter.id))
			// The multi-site selector is not part of the filter count (+N badge on the dropdown filter button)
			.filter((filter) => !(isMultiSiteSearchEnabled && filter?.id === siteFilterId))
			.filter((filter) => {
				const isAtlasSelected = selectedProducts.includes(ProductKeys.Atlas);
				const isTypeFilter = filter?.id === universalTypeFilterId;
				if (serveWebPagesInTypeFilter()) {
					// Subtract 1 from selectedFilterCount when Atlas OR WebCrawler is selected as one of the
					// filters won't be displayed to the user
					return !isTypeFilter
						? true
						: !selectedProducts.includes(ProductKeys.Atlas) &&
								!selectedProducts.includes(ProductKeys.AtlassianWebCrawler);
				}
				return isAtlasSelected ? !isTypeFilter : true;
			});

		const { field } = resultsSort;
		const isNotSortedByDefault = field !== SORT_FIELD_KEY.RELEVANCE;
		return {
			selectedFilterCount: selectedFilters.length + (isNotSortedByDefault ? 1 : 0),
		};
	},
});

export const useFilterAttributesForTracking: HookStateFunction<FilterAttributes, void> =
	createStateHook(Store, {
		selector: ({ filters }) => {
			const filterAttributes = Object.values(filters)
				.flatMap((filter) => {
					return filter ? [filter] : [];
				})
				.map((filter): FilterAttributes => {
					const filterType = filter.type;
					switch (filterType) {
						case 'boolean':
							return {
								filters: [filter.id],
								filterSelections: filter.value
									? [
											{
												value: toEmittableFilterValue(filter.id, String(filter.value)),
												isSmartDefault: false,
												filterId: filter.id,
											},
										]
									: [],
							};
						case 'multi-select':
						case 'single-select':
							return {
								filters: [filter.id],
								filterSelections: filter.options
									.filter((filterOption) => filterOption.isSelected)
									.map((filterOption) => ({
										value: toEmittableFilterValue(
											filter.id,
											filterOption.trackingKey || filterOption.value,
										),
										isSmartDefault: filterOption.isSuggested,
										filterId: filter.id,
									})),
							};
						default:
							return filterType satisfies never;
					}
				})
				.filter((attributes) => attributes.filterSelections.length)
				.reduce(
					(acc, curr): FilterAttributes => {
						return {
							filters: [...acc.filters, ...curr.filters],
							filterSelections: [...acc.filterSelections, ...curr.filterSelections],
						};
					},
					{ filters: [], filterSelections: [] },
				);

			return filterAttributes;
		},
	});

export const useCQL: HookStateFunction<string | undefined, void> = createStateHook(Store, {
	selector: ({ cql }) => cql,
});

export const useAnyFilterSelected: HookFunction<
	boolean,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	| {
			includeSelectedProduct: boolean;
	  }
	| undefined
> = createHook(Store, {
	selector: (
		{ selectedProducts, filters, primaryProduct },
		props: { includeSelectedProduct: boolean } | undefined = { includeSelectedProduct: true },
	) => {
		const hasSelectedProduct = selectedProducts[0];

		if (hasSelectedProduct && props.includeSelectedProduct) {
			return true;
		}

		const isMultiSiteSearchEnabled = serveFpsMultiSiteSearchExperiment({
			primaryProduct,
			fireExperimentExposure: false,
		});

		// Clear all button is not displayed if only the site selector is applied
		return Object.values(filters).some(
			(filter) =>
				filter?.hasSelection && !(isMultiSiteSearchEnabled && filter?.id === siteFilterId),
		);
	},
});

export const useLeapQueryResult: HookStateFunction<
	{
		loading: boolean;
		candidates: string[];
		titleIndex: number;
	},
	void
> = createStateHook(Store, {
	selector: ({ leapQuery }) => leapQuery,
});

export const useRequestedBackendExperiments: HookFunction<
	{
		backendExperiments: BackendExperiment;
	},
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => ({
		backendExperiments: state.requestedBackendExperiments,
	}),
});

export const useJsmIncidentFilterIds: HookStateFunction<string[], void> = createStateHook(Store, {
	selector: (state) => state.jsmIncidentFilterIds,
});

export const useFilterDropdownOpenByQueryParam: HookFunction<
	boolean,
	BoundActions<
		SearchState,
		{
			readonly loadInitialSelectFilterOptions: (props: {
				filterId: string;
				intl: IntlShape;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearSelectFilterOptions: (props: {
				filterId: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * Sets a select filter option.
			 *
			 * @param validateDependentFilterId - If provided, validates if the dependent filter's containerARI
			 *                                    is still in the new selection values and clears it if not.
			 */
			readonly setSelectFilterOption: (props: {
				filterId: string;
				optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
				options?: {
					customValue?: Record<string, string>;
					/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
					setSelectedProduct?: ProductKeys;
					/** If provided, clears the dependent filter if its containerARI is not in the new selection */
					validateDependentFilterId?: string;
				};
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setAutoFilters: (
				filters: Array<{
					filterId: string;
					optionChange: SelectFilterOptionChange | SelectFilterOptionChange[];
					options?: {
						customValue?: Record<string, string>;
					};
				}>,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setSelectFilterLookupInput: (props: {
				filterId: string;
				query: string;
			}) => Action<SearchState, SearchStoreContainerProps>;
			readonly setBooleanFilterValue: (
				props: {
					filterId: string;
					value: boolean;
					options?: {
						/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
						setSelectedProduct?: ProductKeys;
					};
				},
				privateActions?: {
					readonly initialiseStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly updateStore: () => Action<SearchState, SearchStoreContainerProps>;
					readonly setFilter: (
						state: AllFilterStates,
						options?: {
							shouldSetQueryParams?: boolean;
							/** NOTE: Setting setSelectedProduct to undefined does not deselect a product */
							setSelectedProduct?: ProductKeys;
						},
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly updateFilterSelectionOrder: (
						filterId: string,
						selected: boolean,
					) => Action<SearchState, SearchStoreContainerProps>;
					readonly setNewFilterStates: (
						newFilterStates: Record<string, AllFilterStates | undefined>,
						setSelectedProduct?: ProductKeys,
						filterSelectionOrder?: string[],
					) => Action<SearchState, SearchStoreContainerProps>;
				},
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllFilters: () => Action<SearchState, SearchStoreContainerProps>;
			readonly clearAllAvailableFilters: () => Action<SearchState, SearchStoreContainerProps>;
			// Product filter actions
			readonly selectProductFilter: (
				productKey?: ProductKeys,
				datasource?: ThirdPartyConfigsWithAPIResponse | string,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly unselectProductFilter: () => Action<SearchState>;
			// Search Input Actions
			readonly setInputQuery: (inputQuery: string) => Action<SearchState>;
			readonly setSearchQuery: (searchQuery: string) => Action<SearchState>;
			readonly triggerSearch: () => Action<SearchState>;
			readonly retrySearch: () => Action<SearchState>;
			// Sorting actions
			readonly setSortField: (sortField: ResultsSortField) => Action<SearchState>;
			readonly setSortOrder: (sortOrder: ResultsSortOrder) => Action<SearchState>;
			readonly resetSort: () => Action<SearchState>;
			// Rest of Search Actions
			readonly setQueryUpdatedTime: () => Action<SearchState>;
			readonly resetQueryVersion: () => Action<SearchState>;
			readonly incrementQueryVersion: () => Action<SearchState>;
			readonly setIsLoading: (isLoading: boolean) => Action<SearchState>;
			// Results actions
			readonly setResults: (
				results?: SearchState['results'],
				error?: SearchState['resultError'],
				requestId?: SearchState['requestId'],
				traceId?: SearchState['traceId'],
			) => Action<SearchState>;
			readonly appendMoreResults: (results: AppendResultsArgs) => Action<SearchState>;
			readonly setFetchFromCursor: (cursor: string) => Action<SearchState>;
			readonly setIsLoadingMore: (isLoadingMore: boolean) => Action<SearchState>;
			readonly setJiraIssueFieldsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setJiraIssueDetails: (issueData: HydratedJiraIssue[]) => Action<SearchState>;
			readonly setCompassTeamsLoading: (isLoading: boolean) => Action<SearchState>;
			readonly setCompassTeams: (teamData: TeamDetails[]) => Action<SearchState>;
			readonly resetResults: () => Action<SearchState>;
			readonly setAutoCorrection: (autoCorrection?: string) => Action<SearchState>;
			readonly setLeapQuery: ({
				candidates,
				titleIndex,
				loading,
			}: {
				candidates: string[];
				titleIndex: number;
				loading: boolean;
			}) => Action<SearchState>;
			readonly setProductResultCount: (totalCounts?: ProductCount[]) => Action<SearchState>;
			// For backwards compatibility
			readonly clearHybridFilters: () => Action<SearchState>;
			/**
			 * TODO: QS-9329: Persist auto-applied filters when selecting
			 * * Projects (type=project & product=townsquare)
			 * * Goals (type=goal & product=townsquare)
			 * in the right rail.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Considered unsafe since it's using queryParams to change the state.
			 * Ideally we should set it directly in the state, but that requires introducing a new action.
			 * This is because the cooresponding "Type" filter that this selects isn't available or easy to set from outside
			 * The product=townsquare&type=project will correctly hydrate the filter from the URL after params are set though!
			 *
			 * We hope to improve Atlassian Home / global noun handling after GA
			 *
			 * The last modified and contributors filters are explicitly kept in the query params because they are universal.
			 * Type is also a universal filter, but we couple the type with the product to determine which Townsquare results to show.
			 *
			 * @param value: a noun to be set
			 */
			readonly unsafe_setAtlasNouns: (
				value?: NounKeys,
			) => Action<SearchState, SearchStoreContainerProps>;
			/**
			 * * TODO: QS-9329: Persist auto-applied filters when selecting
			 * Web Pages (type=web-page & product=atlassian-web-crawler) in the right rail
			 * or type=web-page in horizontal filters.
			 *
			 * More information: https://product-fabric.atlassian.net/browse/QS-9329
			 *
			 * Similar to unsafe_setAtlasNouns, this directly manipulates URL query params
			 * to set the Type filter to "web-page".
			 */
			readonly unsafe_setWebPageType: () => Action<SearchState, SearchStoreContainerProps>;
			readonly setRequestedBackendExperiments: (
				requestedBackendExperiments: BackendExperiment,
			) => Action<SearchState, SearchStoreContainerProps>;
			readonly setJsmIncidentFilterIds: (
				jsmIncidentFilterIds: string[],
			) => Action<SearchState, SearchStoreContainerProps>;
			/*
			 * This action is only triggered by QF but the value of filterDropdownOpen is only used by FPS
			 * This means we need to update query param to make it available to FPS
			 * That's why updateQueryParams is set to 'true'
			 *
			 * We only call it for clearing and the "set" value would be handled by onNavigate
			 * */
			readonly unsetFilterDropdownOpen: () => Action<SearchState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => {
		return state.filterDropdownOpenByQueryParam;
	},
});
